<template>
  <div class="container-xxl py-4">
    <div class="d-flex align-items-baseline justify-content-between mb-3">
      <div>
        <h1 class="h2 mb-1">Dashboard</h1>
        <p class="text-muted mb-0">Ringkasan penjualan & pelanggan.</p>
      </div>
    </div>
    <div class="d-flex gap-2 mb-3 align-items-center">
      <label class="text-muted small mb-0">Periode:</label>
      <select v-model="selectedMonth" @change="fetchStats" class="form-select w-auto">
        <option
          v-for="m in monthOptions"
          :key="m.value"
          :value="m.value"
        >
          {{ m.label }}
        </option>
      </select>
    </div>

   <!-- KPI cards -->
    <div v-if="isOwner" class="kpi-grid mb-4">
      <!-- TOTAL ORDERS -->
      <div class="kpi-card">
        <div class="kpi-top">
          <div class="kpi-label">Total Orders</div>
          <div class="kpi-value">{{ totals.total }}</div>
        </div>

        <div class="kpi-footer">
          <div class="kpi-pill success">
            Paid {{ totals.paid }}
          </div>
          <div class="kpi-pill warning">
            Unpaid {{ totals.unpaid }}
          </div>
        </div>
      </div>

      <!-- REVENUE -->
      <div class="kpi-card">
        <div class="kpi-top">
          <div class="kpi-label">Penjualan Bulan Ini</div>
          <div class="kpi-value">
            Rp{{ toIDR(totals.revenue_month) }}
          </div>
        </div>

        <div class="kpi-footer muted">
          Dari {{ totals.invoices_month }} invoice
        </div>
      </div>

    </div>

    <div class="row g-3">
      <!-- Best Sellers -->
      <div class="col-lg-6">
        <div class="card shadow-sm">
          <div class="card-header bg-white">
            <strong>Best Seller Produk (Bulan Ini)</strong>
          </div>
          <div class="card-body p-0">
            <div v-if="loading" class="p-3">
              <div class="spinner-border spinner-border-sm text-secondary me-2"></div>
              <span class="text-muted">Memuat…</span>
            </div>
            <div v-else class="table-responsive">
              <table class="table mb-0 align-middle">
                <thead class="table-light">
                  <tr>
                    <th style="width:56px;">No</th>
                    <th>Produk</th>
                    <th class="text-end" style="width:20px;">Qty</th>
                    <th class="text-end" style="width:100px;">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="bestSellers.length === 0">
                    <td colspan="4" class="text-muted text-center py-3">Belum ada penjualan bulan ini.</td>
                  </tr>
                  <tr v-for="(p,i) in filteredBestSellers" :key="p.product_name + i">
                    <td class="text-muted">{{ i+1 }}</td>
                    <td>{{ p.product_name }}</td>
                    <td class="text-end">{{ p.qty }}</td>
                    <td class="text-end">Rp{{ toIDR(p.revenue) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Clients -->
      <div class="col-lg-6">
        <div class="card shadow-sm">
          <div class="card-header bg-white">
            <strong>Top Clients (Bulan Ini)</strong>
          </div>
          <div class="card-body p-0">
            <div v-if="loading" class="p-3">
              <div class="spinner-border spinner-border-sm text-secondary me-2"></div>
              <span class="text-muted">Memuat…</span>
            </div>
            <div v-else class="table-responsive">
              <table class="table mb-0 align-middle">
                <thead class="table-light">
                  <tr>
                    <th style="width:56px;">No</th>
                    <th>Customer</th>
                    <th class="text-end" style="width:120px;">Orders</th>
                    <th class="text-end" style="width:160px;">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="topClients.length === 0">
                    <td colspan="4" class="text-muted text-center py-3">Belum ada transaksi bulan ini.</td>
                  </tr>
                  <tr v-for="(c,i) in topClients" :key="c.customer_name + i">
                    <td class="text-muted">{{ i+1 }}</td>
                    <td>{{ c.customer_name }}</td>
                    <td class="text-end">{{ c.orders }}</td>
                    <td class="text-end">Rp{{ toIDR(c.revenue) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger mt-3 mb-0">{{ error }}</div>
  </div>
</template>

<script>
import { api } from '@/utils/api';
import { isOwner } from "@/utils/auth";

export default {
  name: 'DashboardPage',
  data() {
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;

    return {
      loading: true,
      error: '',
      selectedMonth: ym, // 🔥 default bulan ini
      totals: { total: 0, paid: 0, unpaid: 0, revenue_month: 0, invoices_month: 0 },
      bestSellers: [],
      topClients: [],
    };
  },
  computed: {
    monthOptions() {
      const opts = [];
      const now = new Date();

      for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const value = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
        const label = d.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
        opts.push({ value, label });
      }
      return opts;
    },
    filteredBestSellers() {
      return this.bestSellers.filter(p => {
        const name = p.product_name.toLowerCase();
        return !name.includes('ongkir')
            && !name.includes('ongkos')
            && !name.includes('diskon')
            && !name.includes('discount');
      });
    },
    isOwner() {
      return isOwner();
    },
  },

  mounted() { this.fetchStats(); },
  methods: {
    async fetchStats() {
      try {
        this.loading = true;
        this.error = '';

        const qs = new URLSearchParams();
        if (this.selectedMonth) qs.append('month', this.selectedMonth);

        const res = await fetch(api(`dashboard_stats.php?${qs.toString()}`));
        const json = await res.json();

        if (json.success) {
          this.totals = json.totals || this.totals;
          this.bestSellers = json.best_sellers || [];
          this.topClients  = json.top_clients  || [];
        } else {
          this.error = json.message || 'Gagal memuat data';
        }
      } catch (e) {
        console.error(e);
        this.error = 'Terjadi kesalahan jaringan';
      } finally {
        this.loading = false;
      }
    },
    toIDR(n) {
      return (Number(n) || 0).toLocaleString('id-ID');
    },
  },
};
</script>

<style scoped>
/* ===== PAGE BASE ===== */
.container-xxl {
  background: #f8fafc;
  min-height: 100vh;
}

h1.h2 {
  font-weight: 650;
  letter-spacing: -0.4px;
}

.text-muted {
  color: #64748b !important;
}

/* ===== PERIOD SELECT ===== */
.form-select {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 8px 14px;
  font-size: 14px;
  transition: all .15s ease;
}

.form-select:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99,102,241,.12);
}

/* ===== KPI GRID ===== */
.kpi-grid{
  display:grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap:20px;
}

/* ===== KPI CARD ===== */
.kpi-card{
  background:#ffffff;
  border-radius:20px;
  padding:24px;
  box-shadow:0 6px 20px rgba(15,23,42,.05);
  transition:.25s ease;
}

.kpi-card:hover{
  transform:translateY(-4px);
  box-shadow:0 14px 32px rgba(15,23,42,.08);
}

.kpi-top{
  margin-bottom:16px;
}

.kpi-label{
  font-size:.75rem;
  text-transform:uppercase;
  letter-spacing:.08em;
  color:#64748b;
  margin-bottom:6px;
}

.kpi-value{
  font-size:1.8rem;
  font-weight:700;
  letter-spacing:-.5px;
  color:#0f172a;
}

/* ===== KPI FOOTER ===== */
.kpi-footer{
  display:flex;
  gap:10px;
  align-items:center;
  flex-wrap:wrap;
}

.kpi-footer.muted{
  font-size:14px;
  color:#64748b;
}

/* ===== PILLS ===== */
.kpi-pill{
  padding:6px 12px;
  border-radius:999px;
  font-size:12px;
  font-weight:600;
}

.kpi-pill.success{
  background:rgba(22,163,74,.1);
  color:#16a34a;
}

.kpi-pill.warning{
  background:rgba(245,158,11,.12);
  color:#f59e0b;
}

/* ===== CARD TABLES ===== */
.card {
  border-radius: 20px;
  border: none;
  box-shadow: 0 6px 20px rgba(15,23,42,.05);
  transition: all .2s ease;
}

.card:hover {
  box-shadow: 0 12px 30px rgba(15,23,42,.08);
}

.card-header {
  border-bottom: 1px solid #f1f5f9;
  font-weight: 600;
  font-size: 15px;
  padding: 16px 20px;
}

.card-body {
  padding: 0;
}

/* ===== TABLE STYLE ===== */
.table {
  font-size: 14px;
}

.table thead {
  background: #f1f5f9;
}

.table th {
  font-weight: 600;
  font-size: 13px;
  color: #334155;
  padding: 14px 18px;
  border-bottom: 1px solid #e2e8f0;
}

.table td {
  padding: 14px 18px;
  border-top: 1px solid #f1f5f9;
  color: #475569;
}

.table tbody tr {
  transition: background .2s ease;
}

.table tbody tr:hover {
  background: #f8fafc;
}

.table td.text-muted {
  color: #94a3b8 !important;
}

/* ===== SPINNER ===== */
.spinner-border {
  width: 1rem;
  height: 1rem;
}

/* ===== ALERT ===== */
.alert-danger {
  border-radius: 14px;
  border: none;
  background: rgba(220,38,38,.08);
  color: #dc2626;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .kpi-value {
    font-size: 1.3rem;
  }

  .card-header {
    font-size: 14px;
  }

  .table th,
  .table td {
    padding: 10px 12px;
  }
}
</style>
